import { useRef } from 'react';
import { calmTokens as t } from '../../shared/tokens';
import { fmtEUR } from '../../shared/format';
import { fmtRecurrenceShort } from '../../shared/recurrence';
import {
  IconArrowDown,
  IconArrowUp,
  IconTrash,
  IconX,
} from '../../shared/icons';
import CalmModal from '../shared/CalmModal';

export default function SettingsDeleteModal({
  open,
  template,
  onConfirm,
  onClose,
  loading = false,
}) {
  const confirmRef = useRef(null);

  if (!open || !template) return null;

  const isDebit = template.type === 'debit';

  const header = (
    <div style={{ padding: 'var(--confirm-pad) var(--confirm-pad) 0' }}>
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: -120,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 360,
            height: 240,
            background:
              'radial-gradient(closest-side, rgba(252,165,165,0.12), rgba(252,165,165,0) 70%)',
            pointerEvents: 'none',
          }}
        />

        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          aria-label="Fermer"
          title="Fermer"
          style={{
            position: 'absolute',
            top: 14,
            right: 14,
            width: 30,
            height: 30,
            borderRadius: 8,
            background: 'transparent',
            border: `1px solid ${t.border}`,
            color: t.textMute,
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'grid',
            placeItems: 'center',
            padding: 0,
          }}
        >
          <IconX size={14} />
        </button>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            position: 'relative',
            marginBottom: 'var(--confirm-block-mb)',
          }}
        >
          <div
            style={{
              width: 'var(--confirm-icon)',
              height: 'var(--confirm-icon)',
              borderRadius: 20,
              background: t.debitDim,
              border: `1px solid ${t.debitBorder}`,
              display: 'grid',
              placeItems: 'center',
              color: t.debit,
              marginBottom: 'var(--confirm-icon-mb)',
            }}
          >
            <IconTrash size={22} />
          </div>

          <h2
            id="settings-delete-title"
            style={{
              fontFamily: t.fontDisp,
              fontSize: 'var(--confirm-title)',
              fontWeight: 400,
              letterSpacing: -0.5,
              color: t.text,
              margin: 0,
              lineHeight: 1.15,
            }}
          >
            Supprimer{' '}
            <span style={{ fontStyle: 'italic', color: t.debit }}>
              cette récurrente
            </span>
            &nbsp;?
          </h2>

          <p
            style={{
              margin: '10px 0 0',
              fontSize: 13,
              color: t.textDim,
              fontFamily: t.fontUi,
              maxWidth: 320,
              lineHeight: 1.5,
            }}
          >
            Cette action est définitive. Le modèle ne générera plus d&apos;opérations
            dans vos prévisions.
          </p>
        </div>
    </div>
  );

  const footer = (
    <div
      style={{
        display: 'flex',
        gap: 10,
        justifyContent: 'flex-end',
        padding: 'var(--confirm-foot-pad)',
        borderTop: `1px solid ${t.border}`,
        background: t.surface,
      }}
    >
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            style={{
              padding: '10px 18px',
              background: 'transparent',
              border: `1px solid ${t.border}`,
              color: t.textDim,
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 500,
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: t.fontUi,
              letterSpacing: -0.1,
            }}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            ref={confirmRef}
            style={{
              padding: '10px 20px',
              background: t.debit,
              border: `1px solid ${t.debit}`,
              color: '#2a0a0a',
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 600,
              cursor: loading ? 'progress' : 'pointer',
              fontFamily: t.fontUi,
              letterSpacing: -0.1,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              opacity: loading ? 0.75 : 1,
              boxShadow: '0 6px 18px rgba(252,165,165,0.18)',
            }}
          >
            <IconTrash size={13} />
            {loading ? 'Suppression…' : 'Supprimer'}
          </button>
    </div>
  );

  return (
    <CalmModal
      open={open}
      onClose={onClose}
      labelledBy="settings-delete-title"
      zIndex={70}
      maxWidth={420}
      dismissible={!loading}
      initialFocusRef={confirmRef}
      backdropClassName="settings-delete-backdrop"
      cardClassName="settings-delete-card"
      cardStyle={{
        borderRadius: 20,
        boxShadow: '0 40px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(252,165,165,0.04)',
        position: 'relative',
        // Rogne le halo radial débordant ; le défilement est porté par
        // `.calm-modal-body`, il n'est donc pas bloqué par ce clipping.
        overflow: 'hidden',
      }}
      bodyStyle={{ padding: 'var(--confirm-body-pad)' }}
      header={header}
      footer={footer}
    >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '36px minmax(0, 1fr) auto',
            alignItems: 'center',
            gap: 14,
            padding: '14px 16px',
            background: 'rgba(0,0,0,0.25)',
            border: `1px solid ${t.border}`,
            borderRadius: 14,
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: isDebit ? t.debitDim : t.creditDim,
              border: `1px solid ${isDebit ? t.debitBorder : t.creditBorder}`,
              color: isDebit ? t.debit : t.credit,
              display: 'grid',
              placeItems: 'center',
            }}
            aria-hidden="true"
          >
            {isDebit ? <IconArrowUp size={15} /> : <IconArrowDown size={15} />}
          </div>

          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: t.text,
                fontFamily: t.fontUi,
                letterSpacing: -0.15,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={template.title}
            >
              {template.title || 'Sans titre'}
            </div>
            <div
              style={{
                fontSize: 11,
                color: t.textMute,
                fontFamily: t.fontUi,
                marginTop: 3,
              }}
            >
              {fmtRecurrenceShort(template.recurrencePattern)}
              {template.dayOfMonth != null && (
                <>
                  {' '}
                  ·{' '}
                  <span style={{ fontFamily: t.fontMono }}>
                    le {String(template.dayOfMonth).padStart(2, '0')}
                  </span>
                </>
              )}
            </div>
          </div>

          <div
            style={{
              fontFamily: t.fontMono,
              fontSize: 15,
              fontWeight: 500,
              color: isDebit ? t.debit : t.credit,
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: -0.3,
              whiteSpace: 'nowrap',
            }}
          >
            {isDebit ? '− ' : '+ '}
            {fmtEUR(template.amount)}
          </div>
        </div>

      <style>{`
        .settings-delete-backdrop {
          animation: settingsDeleteFade .18s ease-out both;
        }
        .settings-delete-card {
          animation: settingsDeleteRise .22s cubic-bezier(.2,.8,.2,1) both;
        }
        @keyframes settingsDeleteFade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes settingsDeleteRise {
          from { opacity: 0; transform: translateY(6px) scale(.985); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          .settings-delete-backdrop,
          .settings-delete-card { animation: none !important; }
        }
      `}</style>
    </CalmModal>
  );
}
